import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Route as RootRoute } from "@/routes/__root";
import { Logo } from "@/components/brand/Logo";
import { SocialLinks } from "@/components/layout/SocialLinks";
import { GooglePreferredSource } from "@/components/marketing/GooglePreferredSource";
import { pushAuditStart } from "@/lib/analytics";
import { SITE } from "@/content/siteConfig";


const serviceLinks = [
  { to: "/services/google-ads", label: "Google Ads" },
  { to: "/services/meta-ads", label: "Meta Ads" },
  { to: "/services/local-services-ads", label: "Local Services Ads" },
  { to: "/services/youtube-ads", label: "YouTube Ads" },
] as const;

const industryLinks = [
  { to: "/industries/roofing", label: "Roofing" },
  { to: "/industries/plumbing", label: "Plumbing" },
  { to: "/industries/hvac", label: "HVAC" },
  { to: "/industries/electricians", label: "Electricians" },
  { to: "/industries/landscaping", label: "Landscaping" },
  { to: "/industries/pool-service", label: "Pool service" },
  { to: "/industries", label: "All industries →" },
];

const areaLinks = [
  { to: "/phoenix-ppc-agency", label: "Phoenix" },
  { to: "/scottsdale-ppc-agency", label: "Scottsdale" },
  { to: "/mesa-ppc-agency", label: "Mesa" },
  { to: "/chandler-ppc-agency", label: "Chandler" },
] as const;

const companyLinks = [
  { to: "/case-studies", label: "Case studies" },
  { to: "/research", label: "Research" },
  { to: "/glossary", label: "Glossary" },
  { to: "/ai", label: "Company facts" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/privacy", label: "Privacy" },
] as const;

interface RecentPost {
  slug: string;
  title: string;
}

const HEAD_STYLE: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "rgba(251,250,246,0.72)",
};

const LINK_STYLE: React.CSSProperties = {
  color: "rgba(251,250,246,0.85)",
  fontSize: 13.5,
};

function BlogColumnItems({ posts }: { posts: RecentPost[] }) {
  return (
    <ul className="space-y-1.5 md:space-y-2.5">
      {posts.length === 0 && (
        <li>
          <span style={{ color: "rgba(251,250,246,0.6)", fontSize: 13.5 }}>
            First post drops soon.
          </span>
        </li>
      )}
      {posts.map((p) => (
        <li key={p.slug}>
          <Link
            to="/blog/$slug"
            params={{ slug: p.slug }}
            className="footer-post-link transition-colors hover:text-white"
            style={LINK_STYLE}
          >
            {p.title}
          </Link>
        </li>
      ))}
      <li>
        <Link
          to="/blog"
          className="mono transition-colors hover:text-white"
          style={{
            color: "rgba(251,250,246,0.6)",
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          }}
        >
          All posts →
        </Link>
      </li>
    </ul>
  );
}

export function Footer() {
  const { recentPosts } = RootRoute.useLoaderData();

  const columns = [
    { head: "Services", items: serviceLinks as ReadonlyArray<{ to?: string; label: string }> },
    { head: "Industries", items: industryLinks as ReadonlyArray<{ to?: string; label: string }> },
    { head: "Areas we serve", items: areaLinks as ReadonlyArray<{ to?: string; label: string }> },
    { head: "Company", items: companyLinks as ReadonlyArray<{ to?: string; label: string }> },
  ];

  return (
    <footer className="relative" style={{ background: "#002FA7", color: "#fbfaf6" }}>
      <div className="wrap" style={{ paddingTop: 80, paddingBottom: 48 }}>
        <div className="grid grid-cols-1 gap-12 md:grid-cols-[1.4fr_3fr]">
          <div>
            <Logo variant="white" className="h-[30px] md:h-10 w-auto" />
            <p
              className="mt-6 md:max-w-xs"
              style={{ color: "rgba(251,250,246,0.74)", fontSize: 14, lineHeight: 1.6 }}
            >
              Paid media built for businesses that scale. Certified Google Partner and Meta
              Business Partner, Scottsdale, AZ.
            </p>
            <GooglePreferredSource
              className="mt-6"
              variant="button"
              buttonText="Make us preferred on Google"
            />
            <ul
              className="mt-6 space-y-2"
              style={{ color: "rgba(251,250,246,0.74)", fontSize: 13 }}
            >
              <li>
                <a href={SITE.emailHref} className="hover:text-white transition-colors">
                  {SITE.email}
                </a>
              </li>
              <li>
                <a href={SITE.phoneHref} className="hover:text-white transition-colors">
                  {SITE.phoneDisplay}
                </a>
              </li>
              <li>{SITE.locationDisplay} · {SITE.languages}</li>
            </ul>
            <SocialLinks className="mt-6" />
          </div>

          <div className="footer-columns grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {columns.map((col) => (
              <FooterColumn key={col.head} title={col.head}>
                <ul className="space-y-2 md:space-y-2.5">
                  {col.items.map((i) => (
                    <li key={i.label}>
                      {i.to ? (
                        <Link
                          to={i.to as never}
                          className="transition-colors hover:text-white"
                          style={LINK_STYLE}
                        >
                          {i.label}
                        </Link>
                      ) : (
                        <span style={{ color: "rgba(251,250,246,0.6)", fontSize: 13.5 }}>
                          {i.label}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </FooterColumn>
            ))}
            <FooterColumn title="Blog · Latest">
              <BlogColumnItems posts={recentPosts} />
            </FooterColumn>
          </div>
        </div>

        <Link
          to="/audit"
          onClick={() => pushAuditStart("footer")}
          className="mono mt-10 block w-full text-center transition-opacity hover:opacity-90 md:hidden"
          style={{
            background: "#fbfaf6",
            color: "#002FA7",
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            padding: "16px 20px",
            borderRadius: 999,
          }}
        >
          Get your free audit →
        </Link>


        <div
          className="mt-16 flex flex-col items-start justify-between gap-4 border-t pt-6 md:flex-row md:items-center"
          style={{ borderColor: "rgba(251,250,246,0.18)" }}
        >
          <div
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(251,250,246,0.72)",
            }}
          >
            © 2026 Berk Hospitality Group · Certified Google Partner · Meta Business Partner
          </div>
          <a
            href="#top"
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(251,250,246,0.72)",
            }}
          >
            Back to top ↑
          </a>
        </div>
      </div>

      <style>{`
        .footer-post-link {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .google-preferred-button {

          transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease;
          will-change: transform, box-shadow;
        }
        .google-preferred-button:hover {
          transform: scale(1.04);
          border-color: rgba(251,250,246,0.45) !important;
          box-shadow: 0 0 0 1px rgba(251,250,246,0.10), 0 8px 24px rgba(0,0,0,0.25);
        }
      `}</style>
    </footer>
  );
}

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <div className="hidden md:block">
        <div className="mono mb-4" style={HEAD_STYLE}>{title}</div>
        {children}
      </div>
      <details className="footer-column md:hidden">
        <summary className="mono" style={HEAD_STYLE}>{title}</summary>
        <div className="footer-column-links">{children}</div>
      </details>
    </>
  );
}
